import appClient from '../../common/services/AppClient';
import { SecurityService as security } from '../../common/services';
import { Utils } from '../../common/services/Utils';
import { endpoints } from '../../../config';
import {
    TrainingProjectDto,
    TrainingProjectExampleDto,
    TrainigProjectSessionDto,
    TrainingProjectSessionLogDto,
    CreateEditTrainingProjectFormValues,
    CreateEditTrainingProjectExampleFormValues,
    PreparedPackageForImportDto,
    PackageForImportDto,
    ImportTrainingProjectExamplesResult,
    ExistingExampleMappingDto,
    TrainingProjectStatusDto,
    JsonValidationResult,
    TrainingProjectImportResponse,
    CommitImportTrainingProjectRequest
} from '../types';

export default class TrainingProjectsService {
    async getTrainingProjects(projectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects`;

        const resp = await appClient.get<TrainingProjectDto[]>(url);

        return resp.unwrapOr([]);
    }

    async createTrainingProject(projectId: string, data: CreateEditTrainingProjectFormValues) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects`;

        return appClient.post<TrainingProjectDto>(url, data);
    }

    async importTrainingProject(projectId: string, file: File) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/import`;

        // Create FormData to handle file upload
        const formData = new FormData();
        formData.append('file', file);

        return appClient.post<TrainingProjectImportResponse>(url, formData);
    }

    async commitImportTrainingProject(projectId: string, data: CommitImportTrainingProjectRequest) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/import/commit`;

        return appClient.post<TrainingProjectDto>(url, data);
    }

    async updateTrainingProject(
        projectId: string,
        trainingProjectId: string,
        data: CreateEditTrainingProjectFormValues
    ) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}`;

        return appClient.update<TrainingProjectDto>(url, data);
    }

    async deleteTrainingProject(projectId: string, trainingProjectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}`;

        return appClient.delete(url);
    }

    async getTrainingProjectExamples(projectId: string, trainingProjectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/examples`;

        const resp = await appClient.get<TrainingProjectExampleDto[]>(url);

        return resp.unwrapOr([]);
    }

    async createTrainingProjectExample(
        projectId: string,
        trainingProjectId: string,
        data: CreateEditTrainingProjectExampleFormValues
    ) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/examples`;

        return appClient.post<TrainingProjectExampleDto>(url, data);
    }

    async updateTrainingProjectExample(
        projectId: string,
        trainingProjectId: string,
        exampleId: string,
        data: CreateEditTrainingProjectExampleFormValues
    ) {
        const url =
            endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/examples/${exampleId}`;

        return appClient.update<TrainingProjectExampleDto>(url, data);
    }

    async deleteTrainingProjectExample(projectId: string, trainingProjectId: string, exampleId: string) {
        const url =
            endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/examples/${exampleId}`;

        return appClient.delete(url);
    }

    async deleteTrainingProjectExamplesBulk(projectId: string, trainingProjectId: string, exampleIds: string[]) {
        const url =
            endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/examples/bulk-delete`;

        return appClient.post<number>(url, exampleIds);
    }

    async validateTrainingProjectExamplesJson(projectId: string, trainingProjectId: string) {
        const url =
            endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/examples/validate-json`;

        return appClient.post<JsonValidationResult>(url, {});
    }

    async getTrainingProjectSessions(projectId: string, trainingProjectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/sessions`;

        const resp = await appClient.get<TrainigProjectSessionDto[]>(url);

        return resp.unwrapOr([]);
    }

    async getTrainingSession(projectId: string, trainingProjectId: string, sessionId: string) {
        const url =
            endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/sessions/${sessionId}`;

        return appClient.get<TrainigProjectSessionDto>(url);
    }

    async startTraining(trainingProjectId: string) {
        const url = endpoints.modelTrainingUrl + `training-sessions/start`;

        return appClient.post<string>(url, { trainingProjectId });
    }

    async cancelTraining(trainingSessionId: string) {
        const url = endpoints.modelTrainingUrl + `training-sessions/${trainingSessionId}/cancel`;

        return appClient.post(url);
    }

    async streamTrainingLogs<T extends object>(
        trainingSessionId: string,
        onMessage: (data: T) => void,
        onError?: (err: Error) => void
    ) {
        const url = endpoints.modelTrainingUrl + `training-sessions/${trainingSessionId}/logs`;
        return appClient.createStreamingRequest<T>(url, {}, { onMessage, onError });
    }

    async downloadTrainedModel(
        projectId: string,
        trainingProjectId: string,
        trainingSessionId: string,
        modelFilePath: string
    ) {
        security.invoke(token => {
            const baseUrl = `${endpoints.manageUrl}projects/${projectId}`;
            const endpoint = `/training-projects/${trainingProjectId}/sessions/${trainingSessionId}/${encodeURIComponent(modelFilePath)}`;
            const url = `${baseUrl}${endpoint}?access_token=${encodeURIComponent(token)}`;
            window.open(url);
            return Promise.resolve();
        });
    }

    async getTrainingSessionLogs(projectId: string, trainingProjectId: string, trainingSessionId: string) {
        const url =
            endpoints.manageUrl +
            `projects/${projectId}/training-projects/${trainingProjectId}/sessions/${trainingSessionId}/logs`;

        return appClient.get<TrainingProjectSessionLogDto[]>(url);
    }

    async updateSessionNote(projectId: string, trainingProjectId: string, sessionId: string, note: string | null) {
        const url =
            endpoints.manageUrl +
            `projects/${projectId}/training-projects/${trainingProjectId}/sessions/${sessionId}/note`;
        return appClient.update(url, { note });
    }

    async getProjectPackages(projectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/packages`;

        const resp = await appClient.get<PackageForImportDto[]>(url);

        return resp.unwrapOr([]);
    }

    async getTrainingProjectsStatuses(projectId: string) {
        const url = endpoints.manageUrl + `projects/${projectId}/training-projects/statuses`;

        const resp = await appClient.get<TrainingProjectStatusDto[]>(url);

        return resp.unwrapOr([]);
    }

    async preparePackagesForImport(projectId: string, trainingProjectId: string, packageIds: string[]) {
        const url =
            endpoints.manageUrl +
            `projects/${projectId}/training-projects/${trainingProjectId}/packages/prepare-import`;

        return appClient.post<PreparedPackageForImportDto[]>(url, packageIds);
    }

    async createExamplesFromPackages(
        projectId: string,
        trainingProjectId: string,
        packageIds: string[],
        existingExampleMappings?: ExistingExampleMappingDto[]
    ) {
        const url =
            endpoints.manageUrl +
            `projects/${projectId}/training-projects/${trainingProjectId}/examples/import-from-packages`;

        const payload = {
            packageIds,
            existingExampleMappings
        };

        return appClient.post(url, payload);
    }

    /**
     * Export training project examples as a downloadable file
     * Triggers a file download in the browser
     */
    async exportTrainingProjectExamples(projectId: string, trainingProjectId: string) {
        const mapForm = document.createElement('form');

        mapForm.setAttribute('id', 'exportTrainingProjectExamplesForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';

        await security.invoke(token => {
            const t = '?access_token=' + encodeURIComponent(token);
            const url =
                endpoints.manageUrl +
                `projects/${projectId}/training-projects/${trainingProjectId}/examples/export${t}`;
            Utils.downloadFile(url, mapForm, 'exportTrainingProjectExamplesForm');
            return Promise.resolve();
        });
    }

    async exportTrainingProject(projectId: string, trainingProjectId: string) {
        const mapForm = document.createElement('form');

        mapForm.setAttribute('id', 'exportTrainingProjectForm');
        mapForm.target = '_blank';
        mapForm.method = 'POST';

        await security.invoke(token => {
            const t = '?access_token=' + encodeURIComponent(token);
            const url = endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/export${t}`;
            Utils.downloadFile(url, mapForm, 'exportTrainingProjectForm');
            return Promise.resolve();
        });
    }

    async importTrainingProjectExamples(
        projectId: string,
        trainingProjectId: string,
        file: File
    ): Promise<ImportTrainingProjectExamplesResult> {
        const url =
            endpoints.manageUrl + `projects/${projectId}/training-projects/${trainingProjectId}/examples/import`;

        const formData = new FormData();
        formData.append('file', file);

        const resp = await appClient.post<ImportTrainingProjectExamplesResult>(url, formData);

        if (resp.isErr()) {
            throw resp.error;
        }

        return resp.unwrapOr({
            totalCount: 0,
            importedCount: 0,
            skippedCount: 0,
            duplicateCount: 0,
            validationFailureCount: 0,
            validationErrors: []
        });
    }
}
